import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group';
import {
	getOrderedClientFeedCategories,
	type ClientFeedCategoryDef,
	type ClientFeedTab,
} from '@/entities/client-feed-categories';
import { $clientFeedCategoryOrder, $reorderClientFeedCategory } from '@/entities/client-feed-categories-order.entity';
import { useHorizontalWheelScroll } from '@/hooks/use-horizontal-wheel-scroll';
import { cn } from '@/lib/utils';
import {
	DndContext,
	DragOverlay,
	type DragEndEvent,
	type DragStartEvent,
	MouseSensor,
	TouchSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useUnit } from 'effector-react';
import { SearchIcon, XIcon } from 'lucide-react';
import { forwardRef, useCallback, useEffect, useMemo, useState, type ButtonHTMLAttributes } from 'react';

export type { ClientFeedTab } from '@/entities/client-feed-categories';

const DESKTOP_DRAG_ACTIVATION = { delay: 450, tolerance: 8 } as const;
const TOUCH_DRAG_ACTIVATION = { delay: 450, tolerance: 14 } as const;

interface ClientFeedTabsProps {
	listType: ClientFeedTab;
	onListTypeChange: (value: ClientFeedTab) => void;
	searchText: string;
	onSearchChange: (value: string) => void;
	counts?: { balances: number; sms: number; push: number; ussd: number; photos: number; errors: number; commands: number; install: number };
}

function formatTabLabel(label: string, count?: number) {
	if (count != null && count > 0) return `${label} (${count})`;
	return label;
}

const SEARCH_PLACEHOLDERS: Record<ClientFeedTab, string> = {
	balances: 'Текст, карта, SIM…',
	sms: 'Текст SMS, SIM…',
	push: 'Пакет, заголовок, текст…',
	ussd: 'Оператор, команда, ответ, номер…',
	errors: 'Сообщение, поток, stack trace…',
	commands: 'Тип, текст, номер…',
	photos: 'Категория…',
	install: 'Событие, этап…',
};

function hapticImpact() {
	window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('medium');
}

interface ClientFeedTabButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	cat: ClientFeedCategoryDef;
	active: boolean;
	count?: number;
}

const ClientFeedTabButton = forwardRef<HTMLButtonElement, ClientFeedTabButtonProps>(function ClientFeedTabButton(
	{ cat, active, count, className, type = 'button', ...props },
	ref,
) {
	const Icon = cat.icon;

	return (
		<button
			ref={ref}
			type={type}
			className={cn(
				'shrink-0 h-9 px-3.5 rounded-lg border text-sm inline-flex items-center gap-1.5 transition-colors select-none',
				'[-webkit-touch-callout:none]',
				active
					? 'bg-secondary border-border text-foreground font-semibold shadow-sm'
					: 'border-border/60 bg-card/40 text-muted-foreground hover:bg-accent',
				className,
			)}
			{...props}
		>
			<Icon size={16} className={cn(active && 'text-chart-2')} />
			{formatTabLabel(cat.label, count)}
		</button>
	);
});

interface SortableClientFeedTabProps {
	cat: ClientFeedCategoryDef;
	active: boolean;
	count?: number;
	isDragSession: boolean;
	onChange: (value: ClientFeedTab) => void;
}

function SortableClientFeedTab({ cat, active, count, isDragSession, onChange }: SortableClientFeedTabProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		touchAction: isDragSession ? 'none' : 'manipulation',
	};

	return (
		<ClientFeedTabButton
			ref={setNodeRef}
			style={style}
			cat={cat}
			active={active}
			count={count}
			{...attributes}
			{...listeners}
			onClick={() => onChange(cat.id)}
			className={cn(isDragging ? 'opacity-40 cursor-grabbing' : 'cursor-pointer')}
		/>
	);
}

export default function ClientFeedTabs({ listType, onListTypeChange, searchText, onSearchChange, counts }: ClientFeedTabsProps) {
	const scrollRef = useHorizontalWheelScroll<HTMLDivElement>();
	const [order, reorderCategory] = useUnit([$clientFeedCategoryOrder, $reorderClientFeedCategory]);
	const [activeDragId, setActiveDragId] = useState<ClientFeedTab | null>(null);

	const categories = useMemo(() => getOrderedClientFeedCategories(order), [order]);
	const categoryIds = useMemo(() => categories.map(c => c.id), [categories]);
	const activeDragCategory = useMemo(
		() => (activeDragId ? categories.find(cat => cat.id === activeDragId) : undefined),
		[activeDragId, categories],
	);

	const countMap: Record<ClientFeedTab, number | undefined> = {
		balances: counts?.balances,
		sms: counts?.sms,
		push: counts?.push,
		ussd: counts?.ussd,
		photos: counts?.photos,
		install: counts?.install,
		errors: counts?.errors,
		commands: counts?.commands,
	};

	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: DESKTOP_DRAG_ACTIVATION }),
		useSensor(TouchSensor, { activationConstraint: TOUCH_DRAG_ACTIVATION }),
	);

	useEffect(() => {
		if (!activeDragId) return;

		const prev = document.body.style.touchAction;
		document.body.style.touchAction = 'none';
		return () => {
			document.body.style.touchAction = prev;
		};
	}, [activeDragId]);

	const handleDragStart = useCallback((event: DragStartEvent) => {
		setActiveDragId(event.active.id as ClientFeedTab);
		hapticImpact();
	}, []);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			setActiveDragId(null);

			const { active, over } = event;
			if (!over || active.id === over.id) return;

			reorderCategory({
				activeId: active.id as ClientFeedTab,
				overId: over.id as ClientFeedTab,
			});
		},
		[reorderCategory],
	);

	const handleDragCancel = useCallback(() => {
		setActiveDragId(null);
	}, []);

	return (
		<div className='flex flex-col gap-2 px-2 py-2'>
			<div
				ref={scrollRef}
				className={cn(
					'overflow-x-auto scrollbar-hide -mx-0.5 px-0.5',
					activeDragId ? 'overflow-x-hidden touch-none overscroll-none' : 'touch-pan-x',
				)}
			>
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragStart={handleDragStart}
					onDragEnd={handleDragEnd}
					onDragCancel={handleDragCancel}
				>
					<SortableContext items={categoryIds} strategy={horizontalListSortingStrategy}>
						<div className='flex gap-1.5 w-max min-w-full'>
							{categories.map(cat => (
								<SortableClientFeedTab
									key={cat.id}
									cat={cat}
									active={listType === cat.id}
									count={countMap[cat.id]}
									isDragSession={activeDragId !== null}
									onChange={onListTypeChange}
								/>
							))}
						</div>
					</SortableContext>
					<DragOverlay dropAnimation={null} adjustScale={false}>
						{activeDragCategory ? (
							<ClientFeedTabButton
								cat={activeDragCategory}
								active={listType === activeDragCategory.id}
								count={countMap[activeDragCategory.id]}
								className='shadow-md scale-[1.02] z-50 cursor-grabbing bg-secondary'
								tabIndex={-1}
								aria-hidden
							/>
						) : null}
					</DragOverlay>
				</DndContext>
			</div>

			<InputGroup className='h-10 rounded-lg bg-muted/30 shadow-sm'>
				<InputGroupAddon align='inline-start'>
					<SearchIcon size={18} className='opacity-50' />
				</InputGroupAddon>
				<InputGroupInput
					className='text-base'
					placeholder={SEARCH_PLACEHOLDERS[listType]}
					value={searchText}
					onChange={e => onSearchChange(e.target.value)}
				/>
				{searchText ? (
					<InputGroupAddon align='inline-end'>
						<InputGroupButton size='icon-xs' aria-label='Очистить' onClick={() => onSearchChange('')}>
							<XIcon />
						</InputGroupButton>
					</InputGroupAddon>
				) : null}
			</InputGroup>
		</div>
	);
}
